import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DOCLINK_COLORS, COMMON_COLORS } from "../config/colors";

const MuiDataGrid = ({ data, selectionModel, onSelectionChange }) => {
  const navigate = useNavigate();
  //const [selectionModel, setSelectionModel] = React.useState([]);
  const theme = createTheme({
    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: COMMON_COLORS.WHITE,
            borderRadius: "8px",
          },
          checkboxInput: {
            color: `${DOCLINK_COLORS.MID_PURPLE} !important`,
          },
          row: {
            "&:hover": {
              backgroundColor: DOCLINK_COLORS.PEACH_TINT,
            },
            "&.Mui-selected": {
              backgroundColor: DOCLINK_COLORS.LAVENDER_TINT,
              "&:hover": {
                backgroundColor: DOCLINK_COLORS.LAVENDER_TINT,
              },
            },
          },
          cell: {
            "&:focus": {
              outline: "none",
            },
            "&.MuiDataGrid-cellCheckbox": {
              "&:focus-within": {
                outline: "none !important",
              },
            },
          },
          footerContainer: {
            backgroundColor: COMMON_COLORS.LIGHT_GRAY,
          },
          columnHeaders: {
            "& .super-app-theme--header": {
              backgroundColor: DOCLINK_COLORS.DARK_PURPLE,
              color: COMMON_COLORS.WHITE,
            },
            "& .MuiDataGrid-columnHeaderCheckbox": {
              "&:focus-within": {
                outline: "none !important",
              },
            },
            "& .MuiDataGrid-sortIcon": {
              color: `${COMMON_COLORS.WHITE} !important`,
            },
            "& .MuiDataGrid-menuIconButton": {
              color: `${COMMON_COLORS.WHITE} !important`,
            },
            "& .MuiDataGrid-columnHeader--sorted": {
              border: `2px solid ${DOCLINK_COLORS.LAVENDER_TINT}`,
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
              {
                border: `2px solid ${DOCLINK_COLORS.LAVENDER_TINT}`,
                outline: "none !important",
              },
          },
          columnSeparator: {
            "&.MuiDataGrid-columnSeparator--resizable": {
              color: DOCLINK_COLORS.ORANGE_LIGHT,
            },
            "&.MuiDataGrid-columnSeparator--sideRight": {
              color: DOCLINK_COLORS.ORANGE_LIGHT,
            },
          },
        },
      },
    },
  });

  const centeredColumnProps = {
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  };

  const columns = [
    {
      field: "chemistName",
      headerName: "Chemist Name",
      width: "150",
      flex: 1,
      ...centeredColumnProps,
    },
    {
      field: "TotalRevenue",
      headerName: "Revenue ($)",
      width: "150",
      ...centeredColumnProps,
      renderCell: (params) => {
        const value = parseFloat(params.row.TotalRevenue) || 0;
        return `$${value.toFixed(2)}`;
      },
    },
    {
      field: "TotalBookings",
      headerName: "Bookings",
      width: "120",
      ...centeredColumnProps,
    },
    {
      field: "TotalConsults",
      headerName: "Consults",
      width: "120",
      ...centeredColumnProps,
    },
    {
      field: "TotalScriptRequests",
      headerName: "Rx Requests",
      width: "120",
      ...centeredColumnProps,
    },

    {
      field: "scriptRequestPrice",
      headerName: "Script Price ($)",
      width: "150",
      ...centeredColumnProps,
      renderCell: (params) => {
        const value = parseFloat(params.row.scriptRequestPrice) || 0;
        return `$${value.toFixed(2)}`;
      },
    },
    {
      field: "ConsultPrice",
      headerName: "Consult Price ($)",
      width: "150",
      ...centeredColumnProps,
      renderCell: (params) => {
        const value = parseFloat(params.row.ConsultPrice) || 0;
        return `$${value.toFixed(2)}`;
      },
    },
    {
      field: "AvgWaitTime",
      headerName: "Avg Wait Time (min)",
      width: "150",
      ...centeredColumnProps,
      renderCell: (params) => {
        const value = parseFloat(params.row.AvgWaitTime) || 0;
        return value.toFixed(2);
      },
    },
    // {
    //   field: "TopStaffName",
    //   headerName: "Top Staff",
    //   width: "150",
    //   ...centeredColumnProps,
    // },
    {
      field: "WeeklyAvgConsults",
      headerName: "Avg. Appt/Week",
      width: "150",
      ...centeredColumnProps,
      renderCell: (params) => {
        const value = parseFloat(params.row.WeeklyAvgConsults) || 0;
        return value.toFixed(2);
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      ...centeredColumnProps,
      renderCell: (params) => (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate(`/doclink-analytics/${params.row.PHUUID}`)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: 1,
              backgroundColor: DOCLINK_COLORS.MID_PURPLE,
              minWidth: 68,
              height: 32,
              lineHeight: 1,
              padding: "0 12px",
              "&:hover": {
                boxShadow: 3,
                backgroundColor: DOCLINK_COLORS.DARK_PURPLE,
              },
            }}
          >
            Open
          </Button>
        </div>
      ),
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <div className="flex justify-center items-center py-4">
        <div
          className="bg-white text-black shadow rounded-xl p-2 border-4 border-purple-light w-[100%] max-w-full"
          style={{ width: "100%" }}
        >
          <h3 className="text-lg font-bold text-center mb-0 text-black">
            Chemists registered to group
          </h3>
          <div style={{ width: "100%", height: 520 }}>
            <DataGrid
              rows={data || []}
              columns={columns}
              hideFooterPagination={true}
              checkboxSelection
              disableColumnMenu
              disableColumnReorder
              disableColumnResize
              onRowSelectionModelChange={(newSelection) =>
                onSelectionChange(newSelection)
              }
            />
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default MuiDataGrid;
